// src/services/RoyaltyDistributionService.js
// Distributes royalty when a subscription activation fee is purchased (499 / 649 / 699)
import {
  collection,
  addDoc,
  updateDoc,
  doc,
  query,
  where,
  getDocs,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase/config";
import MultiLevelCommissionService from "./MultiLevelCommissionService";
import {
  getRoyaltyAmountsForActivationFee,
  isAutoFillProgramUpgradePurchase,
  purchaseDataQualifiesForActivationRoyalty,
} from "../config/activationRoyaltyConfig";

class RoyaltyDistributionService {
  static MAX_LEVELS = 10;

  /**
   * @param {Object} purchaseData
   * @param {number} purchaseData.activationFee - 499 (legacy) or current-tier (649/699)
   * @param {boolean} [purchaseData.isAutoFillUpgrade] - if true, no royalty (₹150 upgrade)
   */
  static async distributeRoyalty(purchaseData) {
    try {
      const {
        buyerId,
        buyerName,
        buyerEmail,
        packageId,
        packageName,
        activationFee,
        orderId,
        isAutoFillUpgrade,
      } = purchaseData;

      if (
        isAutoFillProgramUpgradePurchase({
          isAutoFillUpgrade,
          packageId,
          packageCategory: purchaseData.packageCategory,
          packagePrice: purchaseData.packagePrice,
        })
      ) {
        console.log(
          "⏭️ Skipping royalty — Auto Fill upgrade (activation royalty already paid on membership)"
        );
        return {
          success: true,
          distributed: false,
          message: "Auto fill upgrade does not trigger activation royalty",
        };
      }

      if (!purchaseDataQualifiesForActivationRoyalty(purchaseData)) {
        console.log(
          "⏭️ Skipping royalty — order is not an eligible activation fee purchase"
        );
        return {
          success: true,
          distributed: false,
          message: "Not eligible for activation royalty",
        };
      }

      if (
        purchaseData.referralSharingBlocked === true ||
        purchaseData.appliedDiscounts?.usedCashback === true ||
        (purchaseData.appliedDiscounts?.cashbackDiscount || 0) > 0
      ) {
        console.log(
          "Skipping royalty distribution — cashback discount was applied (Points Earnings may still be used by buyer)"
        );
        return {
          success: true,
          distributed: false,
          message: "Cashback discount applied — referral sharing disabled",
        };
      }

      if (orderId) {
        const existing = await getDocs(
          query(
            collection(db, "royaltyRecords"),
            where("orderId", "==", orderId)
          )
        );
        if (!existing.empty) {
          console.log(
            `⏭️ Royalty already distributed for order ${orderId}, skipping`
          );
          return {
            success: true,
            distributed: false,
            message: "Royalty already distributed for this order",
            alreadyDistributed: true,
          };
        }
      }

      const royaltyAmounts = getRoyaltyAmountsForActivationFee(activationFee);
      if (!royaltyAmounts) {
        console.log(
          `⏭️ Skipping royalty distribution - activation fee is ${activationFee}, not eligible (499 / 649 / 699)`
        );
        return {
          success: true,
          distributed: false,
          message: "Activation fee is not eligible for royalty distribution",
        };
      }

      console.log(`💰 Starting royalty distribution for package purchase:`, {
        buyerId,
        buyerName,
        packageId,
        activationFee,
        orderId,
      });

      const referralChain = await MultiLevelCommissionService.getUserReferralChain(
        buyerId,
        this.MAX_LEVELS
      );

      if (referralChain.length === 0) {
        console.log("⏭️ No referral chain found, skipping royalty distribution");
        return {
          success: true,
          distributed: false,
          message: "No referral chain found",
        };
      }

      const distributionResults = {
        success: true,
        distributed: true,
        totalDistributed: 0,
        distributions: [],
        errors: [],
      };

      for (const referrer of referralChain) {
        const level = referrer.level;
        const royaltyAmount = royaltyAmounts[level];

        if (!royaltyAmount) {
          console.log(`⏭️ Skipping level ${level} - no royalty amount defined`);
          continue;
        }

        if (!referrer.hasPurchasedPackage) {
          console.log(
            `⏭️ Skipping level ${level} (${referrer.referrerName}) - not an Ultra customer`
          );
          continue;
        }

        try {
          const referrerQuery = query(
            collection(db, "users"),
            where("uid", "==", referrer.referrerId)
          );
          const referrerSnapshot = await getDocs(referrerQuery);

          if (referrerSnapshot.empty) {
            console.error(`❌ Referrer not found: ${referrer.referrerId}`);
            distributionResults.errors.push({
              level,
              referrerId: referrer.referrerId,
              error: "Referrer not found",
            });
            continue;
          }

          const referrerDoc = referrerSnapshot.docs[0];
          const referrerData = referrerDoc.data();
          const currentRoyaltyBalance = referrerData.royaltyBalance || 0;
          const newRoyaltyBalance = currentRoyaltyBalance + royaltyAmount;

          await updateDoc(doc(db, "users", referrerDoc.id), {
            royaltyBalance: newRoyaltyBalance,
            royaltyEarned: (referrerData.royaltyEarned || 0) + royaltyAmount,
            updatedAt: new Date().toISOString(),
          });

          const royaltyRecord = {
            referrerId: referrer.referrerId,
            referrerName: referrer.referrerName,
            referrerEmail: referrer.referrerEmail,
            buyerId,
            buyerName,
            buyerEmail,
            packageId,
            packageName,
            orderId,
            level,
            royaltyAmount,
            activationFee,
            status: "credited",
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          };

          await addDoc(collection(db, "royaltyRecords"), royaltyRecord);

          distributionResults.totalDistributed += royaltyAmount;
          distributionResults.distributions.push({
            level,
            referrerId: referrer.referrerId,
            referrerName: referrer.referrerName,
            royaltyAmount,
            newBalance: newRoyaltyBalance,
          });

          console.log(
            `✅ Level ${level}: Distributed ₹${royaltyAmount} to ${referrer.referrerName} (New balance: ₹${newRoyaltyBalance})`
          );
        } catch (error) {
          console.error(`❌ Error distributing royalty to level ${level}:`, error);
          distributionResults.errors.push({
            level,
            referrerId: referrer.referrerId,
            error: error.message,
          });
        }
      }

      console.log(
        `💰 Royalty distribution complete. Total distributed: ₹${distributionResults.totalDistributed}`
      );
      return distributionResults;
    } catch (error) {
      console.error("❌ Error in royalty distribution:", error);
      return {
        success: false,
        distributed: false,
        error: error.message,
      };
    }
  }
}

export default RoyaltyDistributionService;
